<template>
  <div class="pp-editor" :style="{ '--pp-color': companyColor }">
    <!-- Carregando / erro -->
    <div v-if="!draft && (loading || !loadFailed)" class="pp-editor__center">
      <p class="pp-editor__muted">Carregando seu perfil público...</p>
    </div>
    <div v-else-if="!draft" class="pp-editor__center">
      <p class="pp-editor__muted">Não conseguimos abrir o editor agora.</p>
      <div class="pp-editor__center-actions">
        <button type="button" class="pp-btn" @click="goBack('back_button')">Voltar para o perfil</button>
        <button type="button" class="pp-btn pp-btn--primary" @click="retryLoad">Tentar de novo</button>
      </div>
    </div>

    <!-- Escolha de modelo (primeira vez ou "Ver modelos") -->
    <template-chooser
      v-else-if="showChooser"
      :current="draft.template"
      :pro-templates="proTemplates"
      :is-pro="isPro"
      @back="onChooserBack"
      @choose="chooseTemplate"
    />

    <!-- Editor no celular: previa + barra de ferramentas embaixo -->
    <div v-else-if="isMobile" class="pp-editor-m">
      <header class="pp-editor-m__bar">
        <button type="button" class="pp-back pp-editor-m__back" @click="goBack('back_button')">
          <chevron-left-icon size="22" /> Perfil público
        </button>
        <span class="pp-editor-m__save" :class="`pp-editor__save--${saveStatus}`">{{ saveShort }}</span>
        <button type="button" class="pp-btn" :class="publishButtonClass" :disabled="publishing" @click="onPublishClick">
          {{ publishing ? "Publicando..." : publishLabel }}
        </button>
      </header>
      <main class="pp-editor-m__preview">
<div v-if="needsPro" class="pp-demo">
          <zap-icon size="16" />
          <span>
            <strong>Modo demonstração.</strong> Você está vendo seu perfil com recursos PRO. Isso não
            fica salvo nem aparece no seu link.
          </span>
          <button type="button" class="pp-btn pp-btn--pro pp-btn--small" @click="openPaywall('demo_banner')">
            Liberar com o PRO
          </button>
        </div>
        <div v-if="templatePending && !needsPro" class="pp-demo pp-demo--template">
          <eye-icon size="16" />
          <span>
            Você está vendo o modelo <strong>{{ draftTemplateName }}</strong>. Seu perfil no ar continua
            no <strong>{{ liveTemplateName }}</strong>.
          </span>
          <button type="button" class="pp-btn pp-btn--small" @click="revertTemplate">Voltar</button>
          <button type="button" class="pp-btn pp-btn--primary pp-btn--small" :disabled="publishing" @click="applyTemplate">
            Publicar com este modelo
          </button>
        </div>
        <profile-preview mode="fill" :address="shortUrl" />
      </main>
      <nav class="pp-editor-m__tools">
        <button type="button" @click="sheet = 'model'"><layout-icon size="22" />Modelo</button>
        <button type="button" @click="sheet = 'sections'"><sliders-icon size="22" />Seções</button>
        <button type="button" @click="sheet = 'style'"><droplet-icon size="22" />Estilo</button>
        <button type="button" @click="openAnalytics('toolbar')"><bar-chart-2-icon size="22" />Analytics</button>
        <button type="button" @click="openShare('toolbar')"><share-2-icon size="22" />Compartilhar</button>
      </nav>

      <pp-dialog :active="!!sheet" sheet @update:active="sheet = null">
        <template-picker
          v-if="sheet === 'model'"
          :value="draft.template"
          :pro-templates="proTemplates"
          :is-pro="isPro"
          @input="setTemplate"
          @paywall="openPaywall('template_notice')"
        />
        <sections-panel v-else-if="sheet === 'sections'" @paywall="openPaywall" />
        <style-panel v-else-if="sheet === 'style'" @paywall="openPaywall" />
      </pp-dialog>
    </div>

    <!-- Editor no computador -->
    <div v-else class="pp-editor-d">
      <header class="pp-editor-d__bar">
        <button type="button" class="pp-back" @click="goBack('back_button')">
          <chevron-left-icon size="18" /> Voltar para o perfil
        </button>

        <div class="pp-editor-d__center">
          <slug-field @paywall="openPaywall" />
          <div class="pp-editor-d__devices" role="group" aria-label="Prévia">
            <button
              type="button"
              :class="{ active: device === 'desktop' }"
              title="Prévia no computador"
              @click="device = 'desktop'"
            >
              <monitor-icon size="18" />
            </button>
            <button
              type="button"
              :class="{ active: device === 'mobile' }"
              title="Prévia no celular"
              @click="device = 'mobile'"
            >
              <smartphone-icon size="18" />
            </button>
          </div>
        </div>

        <div class="pp-editor-d__actions">
          <span class="pp-editor__save" :class="`pp-editor__save--${saveStatus}`">
            <template v-if="saveStatus === 'error'">
              <alert-circle-icon size="14" /> Não foi possível salvar.
              <button type="button" @click="retrySave">Tentar de novo</button>
            </template>
            <template v-else-if="saveStatus === 'pending' || saveStatus === 'saving'">
              Salvando...
            </template>
            <template v-else><check-icon size="14" /> Alterações salvas</template>
          </span>
          <button type="button" class="pp-btn" @click="openAnalytics('header')">
            <bar-chart-2-icon size="18" /> Analytics
          </button>
          <button type="button" class="pp-btn" @click="openPdf('header')">
            <download-icon size="18" /> Baixar PDF
          </button>
          <!-- No ar em modo demonstracao o botao principal abre o PRO; compartilhar o que ja esta no ar continua livre. -->
          <button
            v-if="isPublished && needsPro"
            type="button"
            class="pp-btn"
            @click="openShare('header')"
          >
            <share-2-icon size="18" /> Compartilhar
          </button>
          <button
            type="button"
            class="pp-btn"
            :class="publishButtonClass"
            :disabled="publishing"
            @click="onPublishClick"
          >
            {{ publishing ? "Publicando..." : publishLabel }}
          </button>
        </div>
      </header>

      <div class="pp-editor-d__body">
        <aside class="pp-editor-d__panel">
          <nav class="pp-editor-d__tabs">
            <button
              v-for="item in tabs"
              :key="item.id"
              type="button"
              :class="{ active: tab === item.id }"
              @click="tab = item.id"
            >
              {{ item.label }}
            </button>
          </nav>
          <div class="pp-editor-d__panel-body">
            <template-picker
              v-if="tab === 'model'"
              :value="draft.template"
              :pro-templates="proTemplates"
              :is-pro="isPro"
              @input="setTemplate"
              @paywall="openPaywall('template_notice')"
            />
            <sections-panel v-else-if="tab === 'sections'" @paywall="openPaywall" />
            <style-panel v-else @paywall="openPaywall" />
          </div>
        </aside>
        <main class="pp-editor-d__preview">
<div v-if="needsPro" class="pp-demo">
            <zap-icon size="16" />
            <span>
              <strong>Modo demonstração.</strong> Você está vendo seu perfil com recursos PRO. Isso não
              fica salvo nem aparece no seu link.
            </span>
            <button type="button" class="pp-btn pp-btn--pro pp-btn--small" @click="openPaywall('demo_banner')">
              Liberar com o PRO
            </button>
          </div>
          <div v-if="templatePending && !needsPro" class="pp-demo pp-demo--template">
            <eye-icon size="16" />
            <span>
              Você está vendo o modelo <strong>{{ draftTemplateName }}</strong>. Seu perfil no ar continua
              no <strong>{{ liveTemplateName }}</strong>.
            </span>
            <button type="button" class="pp-btn pp-btn--small" @click="revertTemplate">Voltar</button>
            <button type="button" class="pp-btn pp-btn--primary pp-btn--small" :disabled="publishing" @click="applyTemplate">
              Publicar com este modelo
            </button>
          </div>
            <profile-preview :mode="device" :address="shortUrl" />
        </main>
      </div>
    </div>

    <share-dialog
      :active.sync="shareOpen"
      source="editor"
      @pro-required="openPaywall($event || 'share')"
      @deleted="onDeleted"
    >
      <template v-if="isMobile" #address>
        <div class="pp-editor__share-address">
          <p class="pp-label">Endereço</p>
          <slug-field @paywall="openPaywall" />
        </div>
      </template>
    </share-dialog>

    <paywall-dialog :active.sync="paywallOpen" :source="paywallSource" />

    <!-- Volta do checkout da Stripe -->
    <pp-dialog
      :active="checkoutState !== null"
      :max-width="440"
      :closable="checkoutState !== 'confirming'"
      @update:active="closeCheckout"
    >
      <div class="pp-checkout">
        <template v-if="checkoutState === 'confirming'">
          <span class="pp-checkout__spinner" />
          <h3>Confirmando seu pagamento...</h3>
          <p>Isso leva alguns segundos. Pode deixar esta tela aberta.</p>
        </template>
        <template v-else-if="checkoutState === 'paid'">
          <span class="pp-checkout__icon pp-checkout__icon--ok"><check-icon size="28" /></span>
          <h3>Tudo certo, seu perfil agora é PRO</h3>
          <p>Modelos, endereço, vídeo, áudio e links PRO já estão liberados.</p>
          <button type="button" class="pp-btn pp-btn--primary" @click="closeCheckout(false)">
            Continuar editando
          </button>
        </template>
        <template v-else-if="checkoutState === 'pending'">
          <span class="pp-checkout__icon"><clock-icon size="28" /></span>
          <h3>Pagamento em processamento</h3>
          <p>
            Assim que o pagamento compensar, o PRO liga sozinho e você recebe um e-mail.
          </p>
          <button type="button" class="pp-btn pp-btn--primary" @click="closeCheckout(false)">
            Entendi
          </button>
        </template>
        <template v-else-if="checkoutState === 'failed'">
          <span class="pp-checkout__icon pp-checkout__icon--error"><x-icon size="28" /></span>
          <h3>O pagamento não foi concluído</h3>
          <p>Nada foi cobrado. Você pode tentar de novo quando quiser.</p>
          <button type="button" class="pp-btn pp-btn--primary" @click="retryCheckout">
            Tentar de novo
          </button>
        </template>
      </div>
    </pp-dialog>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import {
  ChevronLeftIcon,
  MonitorIcon,
  SmartphoneIcon,
  CheckIcon,
  AlertCircleIcon,
  DownloadIcon,
  BarChart2Icon,
  ZapIcon,
  EyeIcon,
  LayoutIcon,
  SlidersIcon,
  DropletIcon,
  Share2Icon,
  ClockIcon,
  XIcon,
} from "vue-feather-icons";
import mixin from "@/components/public-profile/mixin";
import PpDialog from "@/components/public-profile/PpDialog.vue";
import TemplateChooser from "@/components/public-profile/TemplateChooser.vue";
import TemplatePicker from "@/components/public-profile/TemplatePicker.vue";
import SectionsPanel from "@/components/public-profile/SectionsPanel.vue";
import StylePanel from "@/components/public-profile/StylePanel.vue";
import ProfilePreview from "@/components/public-profile/ProfilePreview.vue";
import SlugField from "@/components/public-profile/SlugField.vue";
import ShareDialog from "@/components/public-profile/ShareDialog.vue";
import PaywallDialog from "@/components/public-profile/PaywallDialog.vue";
import { displayUrl, isMobileWidth, templateById } from "@/components/public-profile/constants";
import service, { errorReason, errorStatus, pdfUrl } from "@/services/publicProfile";

const CHECKOUT_POLL_MS = 3000;
const CHECKOUT_TIMEOUT_MS = 90 * 1000;
const LEAVE_FLUSH_TIMEOUT_MS = 5000;

export default {
  name: "PublicProfileEditor",
  components: {
    EyeIcon,
    ZapIcon,
    BarChart2Icon,
    PpDialog,
    TemplateChooser,
    TemplatePicker,
    SectionsPanel,
    StylePanel,
    ProfilePreview,
    SlugField,
    ShareDialog,
    PaywallDialog,
    ChevronLeftIcon,
    MonitorIcon,
    SmartphoneIcon,
    CheckIcon,
    AlertCircleIcon,
    DownloadIcon,
    LayoutIcon,
    SlidersIcon,
    DropletIcon,
    Share2Icon,
    ClockIcon,
    XIcon,
  },
  mixins: [mixin],
  data: () => ({
    loadFailed: false,
    tab: "sections",
    device: "desktop",
    isMobile: isMobileWidth(),
    sheet: null,
    shareOpen: false,
    paywallOpen: false,
    paywallSource: "",
    publishing: false,
    checkoutState: null, // confirming | paid | pending | failed
  }),
  computed: {
    ...mapState("publicProfile", ["draft", "loading", "saveStatus"]),
    ...mapGetters("publicProfile", [
      "page",
      "isPro",
      "proTemplates",
      "isPublished",
      "hasEverPublished",
      "needsPro",
    ]),
    tabs: () => [
      { id: "model", label: "Modelo" },
      { id: "sections", label: "Seções" },
      { id: "style", label: "Estilo" },
    ],
    showChooser() {
      return this.$route.query.modelos === "1";
    },
    shortUrl() {
      return displayUrl(this.page && this.page.url);
    },
    publishLabel() {
      if (this.needsPro) return "Publicar com PRO";
      if (this.isPublished && this.templatePending) return "Publicar modelo";
      if (this.isPublished) return "Compartilhar";
      return "Publicar";
    },
    publishButtonClass() {
      return this.needsPro ? "pp-btn--pro" : "pp-btn--primary";
    },
    templatePending() {
      return this.$store.state.publicProfile.templatePending;
    },
    draftTemplateName() {
      return this.draft ? templateById(this.draft.template).name : "";
    },
    liveTemplateName() {
      return this.page ? templateById(this.page.template).name : "";
    },
    saveShort() {
      if (this.saveStatus === "error") return "Não salvou";
      if (this.saveStatus === "pending" || this.saveStatus === "saving") return "Salvando...";
      return "Salvo";
    },
  },
  watch: {
    tab(value, from) {
      this.ppTrack("public_profile_tab_changed", { tab: value, from });
    },
    sheet(value, from) {
      if (value) this.ppTrack("public_profile_mobile_sheet_opened", { sheet: value, from: from || null });
    },
    device(value, from) {
      this.ppTrack("public_profile_preview_device_changed", { device: value, from });
    },
    showChooser: {
      immediate: true,
      handler(value) {
        if (value) this.ppTrack("public_profile_template_chooser_viewed", { first_time: !this.hasEverPublished });
      },
    },
  },
  created() {
    this.openedAt = Date.now();
    // O store e persistido na sessao: comeca limpo para nao editar dado velho.
    const { dirty, demo } = this.$store.state.publicProfile;
    // Voltando do pagamento, a demonstracao (so no rascunho) nao pode ser apagada.
    if (!dirty && !(demo && this.$route.query.checkout)) this.$store.dispatch("publicProfile/clear");
    this.load();
    this.handleCheckoutReturn();
  },
  mounted() {
    window.addEventListener("resize", this.onResize);
    window.addEventListener("beforeunload", this.onBeforeUnload);
  },
  beforeDestroy() {
    this.destroyed = true;
    clearTimeout(this.pollTimer);
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("beforeunload", this.onBeforeUnload);
  },
  async beforeRouteLeave(to, from, next) {
    // Salva o que falta, mas sem prender a navegacao se a API nao responder.
    let timer;
    const timeout = new Promise((resolve) => {
      timer = setTimeout(resolve, LEAVE_FLUSH_TIMEOUT_MS);
    });
    await Promise.race([this.$store.dispatch("publicProfile/flush").catch(() => {}), timeout]);
    clearTimeout(timer);
    this.ppTrack("public_profile_editor_closed", {
      via: this.leaveVia || "navigation",
      to: to.name || null,
      seconds_open: Math.round((Date.now() - this.openedAt) / 1000),
    });
    next();
  },
  methods: {
    async load() {
      this.loadFailed = false;
      try {
        // Voltando do pagamento, mantem a demonstracao para aplicar depois de confirmar.
        await this.$store.dispatch("publicProfile/fetch", { keepDraft: !!this.$route.query.checkout });
        if (!this.openTracked) {
          this.openTracked = true;
          this.ppTrack(
            "public_profile_editor_opened",
            {
              first_time: !this.hasEverPublished,
              from_checkout: this.$route.query.checkout || null,
              with_chooser: this.showChooser,
              entry_source: this.$route.query.origem || null,
            },
            { setPerson: true },
          );
        }
      } catch (e) {
        this.loadFailed = true;
        this.ppTrack("public_profile_editor_load_failed", { status: errorStatus(e), reason: errorReason(e) });
      }
    },
    retryLoad() {
      this.ppTrack("public_profile_editor_load_retry_clicked");
      this.load();
    },
    onResize() {
      this.isMobile = isMobileWidth();
    },
    onDeleted() {
      this.leaveVia = "deleted";
      this.$router.push({ name: "dashboard-profile-detail", params: this.$route.params });
    },
    onBeforeUnload(event) {
      if (this.saveStatus === "pending" || this.saveStatus === "saving") {
        this.$store.dispatch("publicProfile/flush").catch(() => {});
        event.preventDefault();
        event.returnValue = "";
      }
    },
    openAnalytics(source) {
      this.ppTrack("public_profile_analytics_opened", { source: typeof source === "string" ? source : "editor" });
      this.leaveVia = "analytics";
      this.$store.dispatch("publicProfile/flush").catch(() => {});
      this.$router.push({ name: "public-profile-analytics", params: { company_label: this.companyLabel } });
    },
    goBack(source) {
      this.leaveVia = typeof source === "string" ? source : "back_button";
      this.$router.push({
        name: "dashboard-profile-detail",
        params: { company_label: this.companyLabel },
      });
    },
    onChooserBack() {
      this.ppTrack("public_profile_template_chooser_back", { first_time: !this.hasEverPublished });
      if (this.hasEverPublished) this.clearQuery(["modelos"]);
      else this.goBack();
    },
    chooseTemplate(template) {
      const firstTime = !this.hasEverPublished;
      this.ppTrack("public_profile_template_chosen", {
        template,
        template_name: templateById(template).name,
        template_is_pro: this.proTemplates.includes(template),
        first_time: firstTime,
        origin: "template_chooser",
      });
      this.setTemplate(template, "template_chooser");
      if (firstTime) {
        this.ppTrack("public_profile_created", { template, template_is_pro: this.proTemplates.includes(template) }, { setPerson: true });
      }
      this.tab = "sections";
      this.clearQuery(["modelos"]);
    },
    async applyTemplate() {
      this.publishing = true;
      const from = this.page ? this.page.template : null;
      try {
        await this.$store.dispatch("publicProfile/applyTemplate");
        this.ppNotify(`Pronto: seu perfil agora está no modelo ${this.draftTemplateName}.`);
        this.ppTrack(
          "public_profile_template_published",
          { template: this.draft.template, from, template_is_pro: this.proTemplates.includes(this.draft.template) },
          { setPerson: true },
        );
      } catch (e) {
        this.ppTrack("public_profile_template_publish_failed", {
          template: this.draft.template,
          from,
          status: errorStatus(e),
          reason: errorReason(e),
        });
        this.ppError("Não foi possível publicar o modelo agora.");
      } finally {
        this.publishing = false;
      }
    },
    setTemplate(template, origin) {
      if (template === this.draft.template) return;
      const from = typeof origin === "string" ? origin : this.isMobile ? "template_picker_mobile" : "template_picker";
      this.$store.dispatch("publicProfile/edit", { template, _track: { origin: from } });
    },
    // "Voltar" no aviso de modelo so na previa: desiste do modelo novo.
    revertTemplate() {
      this.ppTrack("public_profile_template_reverted", {
        template: this.draft.template,
        back_to: this.page.template,
      });
      this.setTemplate(this.page.template, "pending_banner_back");
    },
    clearQuery(keys) {
      const query = { ...this.$route.query };
      keys.forEach((key) => delete query[key]);
      this.$router.replace({ query }).catch(() => {});
    },
    openPaywall(source = "") {
      this.sheet = null;
      this.shareOpen = false;
      this.paywallSource = source;
      this.paywallOpen = true;
    },
    openShare(source) {
      this.sheet = null;
      this.shareOpen = true;
      this.ppTrack("public_profile_share_opened", { source });
    },
    retrySave() {
      this.ppTrack("public_profile_save_retry_clicked");
      this.$store.dispatch("publicProfile/save").catch(() => {});
    },
    async onPublishClick() {
      let action = "publish";
      if (this.needsPro) action = "paywall";
      else if (this.isPublished && this.templatePending) action = "apply_template";
      else if (this.isPublished) action = "share";
      this.ppTrack("public_profile_publish_clicked", { action, label: this.publishLabel });

      if (action === "paywall") {
        this.openPaywall("publish");
        return;
      }
      if (action === "apply_template") {
        this.applyTemplate();
        return;
      }
      if (action === "share") {
        this.openShare("publish_button");
        return;
      }
      this.publishing = true;
      const firstTime = !this.hasEverPublished;
      try {
        await this.$store.dispatch("publicProfile/publish");
        this.ppTrack(
          "public_profile_published",
          { source: "editor", template: this.draft.template, first_time: firstTime },
          { setPerson: true },
        );
        this.shareOpen = true;
      } catch (error) {
        this.ppTrack("public_profile_publish_failed", {
          source: "editor",
          status: errorStatus(error),
          reason: errorReason(error),
        });
        if (errorStatus(error) === 402 || errorReason(error) === "pro_required") {
          this.openPaywall("publish");
        } else {
          this.ppError("Não conseguimos publicar agora. Tente de novo em instantes.");
        }
      } finally {
        this.publishing = false;
      }
    },
    async openPdf(source) {
      this.ppTrack("public_profile_pdf_opened", { source: typeof source === "string" ? source : "editor" });
      // Abre a aba ja no clique (bloqueador de pop-up) e so depois salva.
      const tab = window.open("", "_blank");
      await this.$store.dispatch("publicProfile/flush").catch(() => {});
      const url = pdfUrl(this.page.path);
      if (tab) tab.location.href = url;
      else window.open(url, "_blank");
    },

    // Volta da Stripe: ?checkout=success&session_id=... ou ?checkout=canceled
    handleCheckoutReturn() {
      const { checkout, session_id: sessionId } = this.$route.query;
      if (checkout === "canceled") {
        this.ppTrack("public_profile_checkout_canceled");
        this.ppNotify("Pagamento cancelado. Nada foi cobrado.", "primary");
        this.clearQuery(["checkout", "session_id"]);
        return;
      }
      if (checkout === "success" && sessionId) {
        this.ppTrack("public_profile_checkout_returned", { session_id: sessionId });
        this.checkoutSession = sessionId;
        this.checkoutStartedAt = Date.now();
        this.checkoutState = "confirming";
        this.pollCheckout();
      }
    },
    async pollCheckout() {
      let status = "pending";
      try {
        const result = await service.checkoutStatus(this.checkoutSession);
        status = (result && result.status) || "pending";
      } catch (e) {
        status = "pending";
      }
      if (this.destroyed || this.checkoutState !== "confirming") return;

      if (status === "paid") {
        await this.$store.dispatch("publicProfile/fetch", { keepDraft: true }).catch(() => {});
        if (this.destroyed) return;
        await this.$store.dispatch("publicProfile/applyDemo").catch(() => {});
        if (this.destroyed) return;
        this.checkoutState = "paid";
        this.ppTrack(
          "public_profile_checkout_paid",
          {
            session_id: this.checkoutSession,
            seconds_to_confirm: Math.round((Date.now() - this.checkoutStartedAt) / 1000),
          },
          { setPerson: true },
        );
        this.clearQuery(["checkout", "session_id"]);
      } else if (status === "failed" || status === "expired") {
        this.checkoutState = "failed";
        this.ppTrack("public_profile_checkout_failed", { session_id: this.checkoutSession, status });
        this.clearQuery(["checkout", "session_id"]);
      } else if (Date.now() - this.checkoutStartedAt >= CHECKOUT_TIMEOUT_MS) {
        this.checkoutState = "pending";
        this.ppTrack("public_profile_checkout_pending_timeout", {
          session_id: this.checkoutSession,
          seconds_waited: Math.round((Date.now() - this.checkoutStartedAt) / 1000),
        });
        this.clearQuery(["checkout", "session_id"]);
      } else {
        clearTimeout(this.pollTimer);
        this.pollTimer = setTimeout(this.pollCheckout, CHECKOUT_POLL_MS);
      }
    },
    closeCheckout(value) {
      if (value) return;
      if (this.checkoutState === "confirming") return;
      this.ppTrack("public_profile_checkout_dialog_closed", { state: this.checkoutState });
      this.checkoutState = null;
    },
    retryCheckout() {
      this.ppTrack("public_profile_checkout_retry_clicked");
      this.checkoutState = null;
      this.openPaywall("checkout_failed");
    },
  },
};
</script>

<style lang="scss">
.pp-demo {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  padding: 10px 12px;
  border: 1px solid #6554c0;
  border-radius: 8px;
  background: #f0eefb;
  color: #333;
  font-family: Roboto, sans-serif;
  font-size: 13px;
  line-height: 1.4;

  svg {
    flex-shrink: 0;
    color: #6554c0;
  }

  span {
    flex: 1;
  }

  .pp-btn {
    flex-shrink: 0;
  }

  // Tela estreita: os botoes descem para a linha de baixo em vez de espremer o texto.
  @media (max-width: 767px) {
    flex-wrap: wrap;

    span {
      flex-basis: calc(100% - 26px);
    }
  }
}

.pp-demo--template {
  border-color: #4169e1;
  background: #eef2fd;

  svg {
    color: #4169e1;
  }
}

.pp-editor {
  min-height: 100vh;
  font-family: Roboto, sans-serif;

  &__center {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    min-height: 100vh;
    padding: 24px;
    text-align: center;
  }

  &__center-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
  }

  &__muted {
    color: #4f4f4f;
    font-size: 16px;
  }

  &__save {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #696969;
    font-size: 14px;
    white-space: nowrap;

    &--saved,
    &--idle {
      svg {
        color: #309e3a;
      }
    }

    &--error {
      color: #de2348;

      button {
        padding: 0;
        border: none;
        background: none;
        color: #de2348;
        font-family: Roboto, sans-serif;
        font-size: 14px;
        text-decoration: underline;
        cursor: pointer;
      }
    }
  }

  &__share-address {
    margin-bottom: 16px;
  }
}

// Computador
.pp-editor-d {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;

  &__bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-shrink: 0;
    height: 64px;
    padding: 0 20px;
    border-bottom: 0.5px solid #a8a8a8;
    background: #fff;
  }

  &__center {
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: 0;
  }

  &__devices {
    display: flex;
    gap: 4px;
    padding: 4px;
    border: 0.5px solid #a8a8a8;
    border-radius: 8px;

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 30px;
      border: none;
      border-radius: 6px;
      background: none;
      color: #4f4f4f;
      cursor: pointer;

      &.active {
        background: #e7edf0;
        color: var(--pp-color);
      }
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__body {
    display: flex;
    flex: 1;
    min-height: 0;
  }

  &__panel {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    width: 380px;
    border-right: 0.5px solid #a8a8a8;
    background: #fff;
  }

  &__tabs {
    display: flex;
    flex-shrink: 0;
    border-bottom: 0.5px solid #a8a8a8;

    button {
      flex: 1;
      height: 52px;
      border: none;
      border-bottom: 2px solid transparent;
      background: #fff;
      color: #696969;
      font-family: Roboto, sans-serif;
      font-size: 16px;
      cursor: pointer;
      transition: all 0.2s ease;

      &.active {
        border-bottom-color: var(--pp-color);
        color: #333;
      }
    }
  }

  &__panel-body {
    flex: 1;
    overflow-y: auto;
    padding: 24px 16px 32px;
  }

  &__preview {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    padding: 24px;
    background: #e7edf0;

    // Avisos (demonstração, modelo só na prévia) em cima; a prévia ocupa o resto.
    > .pp-demo {
      flex-shrink: 0;
    }

    > :last-child {
      flex: 1;
      min-height: 0;
    }
  }

  @media (max-width: 1100px) {
    &__bar {
      padding: 0 12px;
    }

    .pp-editor__save {
      display: none;
    }
  }
}

// Celular
.pp-editor-m {
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  background: #e7edf0;

  &__bar {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    height: 56px;
    padding: 0 12px 0 8px;
    border-bottom: 0.5px solid #a8a8a8;
    background: #fff;
  }

  &__back {
    flex: 1;
    color: #333;
    font-size: 18px;
    font-weight: 500;
  }

  &__save {
    color: #696969;
    font-size: 12px;

    &.pp-editor__save--error {
      color: #de2348;
    }
  }

  // Avisos em cima sem empurrar a previa para baixo da barra: a previa fica com o resto.
  &__preview {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    padding: 16px 16px 12px;

    > .pp-demo {
      flex-shrink: 0;
    }

    > .pp-preview {
      flex: 1;
      min-height: 0;
    }
  }

  &__tools {
    display: flex;
    flex-shrink: 0;
    padding: 8px 0 calc(8px + env(safe-area-inset-bottom));
    border-top: 0.5px solid #a8a8a8;
    background: #fff;

    button {
      display: flex;
      flex: 1;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      border: none;
      background: none;
      color: #4f4f4f;
      font-family: Roboto, sans-serif;
      font-size: 12px;
      cursor: pointer;
    }
  }
}

// Volta do checkout
.pp-checkout {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px 8px 8px;
  text-align: center;

  h3 {
    color: #333;
    font-size: 20px;
    font-weight: 600;
  }

  p {
    color: #4f4f4f;
    font-size: 15px;
  }

  .pp-btn {
    margin-top: 8px;
  }

  &__spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e7edf0;
    border-top-color: #6554c0;
    border-radius: 9999px;
    animation: pp-spin 0.9s linear infinite;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 9999px;
    background: rgba(101, 84, 192, 0.12);
    color: #6554c0;

    &--ok {
      background: #dcfce7;
      color: #166534;
    }

    &--error {
      background: rgba(222, 35, 72, 0.08);
      color: #de2348;
    }
  }
}

@keyframes pp-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
